/**
 * Photos on a work order, and how verification happens at this site.
 *
 * An "after" photograph on a completed order verifies it by itself unless the
 * site has turned that off; otherwise a supervisor or administrator verifies.
 * The completion is what the KPIs count, so a slow verification costs nothing.
 */
import { useRef, useState } from "react";
import s from "./WorkOrders.module.css";
import { useSession } from "../../app/SessionContext";
import { useSetWorkSettings, useUploadWorkOrderPhoto, useWorkSettings } from "../../api/queries";
import { hasPermission, PERMISSION } from "../../api/dataSource";
import { Button, Card } from "../../design/components";
import type { WorkOrder } from "../../domain/workOrders";
import { t } from "../../i18n";

export function WorkOrderPhotos({ wo }: { wo: WorkOrder }) {
  const { principal, source } = useSession();
  const upload = useUploadWorkOrderPhoto(wo.id);
  const settings = useWorkSettings();
  const setSettings = useSetWorkSettings();
  const file = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const canAdd = hasPermission(principal, PERMISSION.evidenceWrite) || hasPermission(principal, PERMISSION.workOrdersWrite);
  const canSwitch = hasPermission(principal, PERMISSION.siteWrite);
  const auto = settings.data?.autoVerifyPhoto ?? true;
  const photos = wo.evidence ?? [];
  const before = photos.filter((p) => p.phase === "before").length;
  const after = photos.filter((p) => p.phase === "after").length;
  const open = !["cancelled", "verified"].includes(wo.status);

  return (
    <Card title="Photos and verification" sub="An after photo can verify finished work" testId="wo-photos" collapsible>
      <div className={s.deliveryFacts}>
        <div>{t("{before} before, {after} after.", { before, after })}</div>
        {wo.status === "verified" && (
          <div style={{ color: "var(--green)", fontWeight: 600 }}>
            {wo.verifiedVia === "photo"
              ? t("Verified automatically from the after photo.")
              : t("Verified by {name}.", { name: wo.verifiedBy ?? t("a supervisor") })}
          </div>
        )}
        {wo.status === "completed" && (
          <div>
            {auto
              ? t("Add an after photo and this order verifies itself. Or a supervisor or administrator can verify it.")
              : t("Waiting for a supervisor or administrator to verify it.")}
          </div>
        )}
        <div className="muted">
          {auto
            ? t("At this site, an after photo verifies completed work automatically.")
            : t("At this site, a person verifies all completed work.")}{" "}
          {canSwitch && (
            <Button
              size="sm"
              variant="ghost"
              disabled={setSettings.isPending || settings.isPending}
              onClick={() => setSettings.mutate({ autoVerifyPhoto: !auto })}
            >
              {auto ? t("Turn off") : t("Turn on")}
            </Button>
          )}
        </div>
      </div>
      {open && (
        <div className={s.actionRow} style={{ marginTop: 10 }}>
          <input
            ref={file}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (!f) return;
              setError(null);
              upload.mutate(
                { file: f, phase: "after" },
                { onError: (err) => setError(err instanceof Error ? err.message : String(err)) },
              );
            }}
          />
          <Button
            size="sm"
            disabled={upload.isPending}
            onClick={() => (canAdd ? file.current?.click() : setError(t("You need the Supervisor or Technician role to change this.")))}
          >
            {upload.isPending ? t("Uploading…") : t("Add after photo")}
          </Button>
        </div>
      )}
      {error && (
        <div className={s.refusal} role="alert">
          {source.kind === "mock" ? t("Refused by the sample: {reason}", { reason: error }) : error}
        </div>
      )}
    </Card>
  );
}
